import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createPaginationContainer, graphql } from 'react-relay';

import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import LoadMoreButton from 'src/components/LoadMoreButton';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';

import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import BadgeActive from 'src/components/Badge/BadgeActive';

/**
 * Description
 * @param {object} props
 * @returns {React.ReactComponentElement} - AdminMeterList component
 */
function AdminMeterList(props) {
  const loadMore = () => {
    const { relay } = props;

    if (!relay.hasMore() || relay.isLoading()) {
      return;
    }

    relay.loadMore(
      50,
      (error) => { console.log(error); },
    );
  };

  if (!props) {
    return <Loading />;
  }

  const { relay, viewer } = props;
  const { meters } = viewer;
  const intl = useIntl();

  const copyLoadMore = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.load_more_button.state_load_more.label', defaultMessage: 'Load more...' });
  const copyNoMore = intl.formatMessage({ id: 'admin.admin_meter.admin_meter_list.load_more_button.state_no_more.label', defaultMessage: 'No more meters to load' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Enosi Admin - Meters</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: 'Admin', path: '/admin' },
          { name: 'Meters' },
        ]}
      />

      <div className="clearfix">
        <h1 className="float-start">Meters</h1>
      </div>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Status</th>
                <th scope="col">Identifier</th>
                <th scope="col">ID</th>
                <th scope="col">External identifier</th>
                <th scope="col">Primary Billing Point</th>
                <th scope="col">Property</th>
              </tr>
            </thead>
            <tbody>
              {
                // properties && <pre>{JSON.stringify(properties, null, 2)}</pre>
                meters && meters.edges && meters.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    active, externalIdentifier, id, identifier, primaryBillingPoint, property,
                  } = node;

                  return (
                    <tr key={`admin-meter-${id}`}>
                      <td>
                        <BadgeActiveTimeRange active={active} />
                      </td>
                      <th scope="row" colSpan={3}>
                        <Link to={`/admin/meters/${id}`}>
                          <div>{identifier}</div>
                        </Link>
                        <UUID uuid={id} />
                        <br />
                        {`(${externalIdentifier})`}
                      </th>
                      <td>
                        <Link to={`/admin/primary-billing-points/${primaryBillingPoint.id}`}>
                          {primaryBillingPoint.identifier}
                        </Link>
                        <br />
                        <UUID uuid={primaryBillingPoint.id} />
                        <br />
                        <BadgeActive
                          start={primaryBillingPoint.active.start}
                          finish={primaryBillingPoint.active.finish}
                        />
                      </td>
                      <td>
                        <Link to={`/admin/properties/${property.id}`}>
                          {property.title}
                        </Link>
                        <br />
                        <UUID uuid={property.id} />
                        <br />
                        <BadgeActive
                          start={property.active.start}
                          finish={property.active.finish}
                        />
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={relay.hasMore()}
          />
        </div>
      </div>
    </>
  );
}

AdminMeterList.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

AdminMeterList.defaultProps = {
  viewer: null,
};

export default createPaginationContainer(
  AdminMeterList,
  {
    viewer: graphql`
      fragment AdminMeterList_viewer on Viewer {
        id
        viewerUser {
          email
          givenName
          familyName
        }
        meters(
          first: $count
          after: $cursor
        ) @connection(key: "AdminMeterList_meters") {
          edges {
            cursor
            node {
              id
              externalIdentifier
              identifier
              tradePointId
              active {
                start
                finish
              }
              property {
                id
                title
                active {
                  start
                  finish
                }
              }
              primaryBillingPoint {
                id
                identifier
                active {
                  start
                  finish
                }
              }
            }
          }
          count
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
        }
      }
    `,
  },
  {
    direction: 'forward',
    getConnectionFromProps(props) {
      return props.viewer.meters;
    },
    // This is also the default implementation of `getFragmentVariables` if it isn't provided.
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }) {
      return {
        count,
        cursor,
      };
    },
    query: graphql`
    # Pagination query to be fetched upon calling 'loadMore'.
    # Notice that we re-use our fragment, and the shape of this query matches our fragment spec.
    query AdminMeterList_Pagination_Query(
      $count: Int!
      $cursor: String
    ) {
      viewer {
        ...AdminMeterList_viewer
      }
    }
  `,
  },
);
